import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import CONFIG from "config/config";

/*================================ APIs ==============================*/
import GetRedmineTokenApi from "issara-sdk/apis/GetRedmineToken_usersM";
import GetCustomFields from "issara-sdk/apis/GetCustomFields_usersM";

import {
  getProjectList,
  getProjectName,
  CreateProjectType,
  CreateProjectPayloadType,
  createProject,
  editProject,
  archiveProject,
  getAllIssuesTracking,
  getTrackerList,
} from "./RedmineAPIController";

import { IssueListType } from "./IssueList";
import moment from "moment";

/*================================ Type-Defining Constants ==============================*/

export const ACTIONS = {
  SEARCH: "SEARCH",
  CREATE: "CREATE",
  EDIT: "EDIT",
  DETAIL: "DETAIL",
  ARCHIVE: "ARCHIVE",
} as const;

export const CARD_NAME = "ProjectList" as const;

/*================================ Types and Interface ==============================*/

export type State = Partial<{
  ProjectListSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    projectList: ProjectListType[];
    customFields: CustomFieldsItemType[];
    trackerList: TrackerListType[];
    issuesTracking: IssueListType[];
    // pagination: { activePage: number; items: any; total: number };
  }> | null;
}>;

export type RedmineTokenResponse = {
  header: string;
  token: string;
};

// Based on Response
export type ProjectListType = Partial<{
  created_on: string;
  description: string;
  homepage: string;
  id: number;
  identifier: string;
  inherit_members: boolean;
  is_public: boolean;
  name: string;
  status: number;
  updated_on: string;
  custom_fields: CustomFieldsType[];
  parent: ParentType;
}>;

export type CustomFieldsItemType = {
  id: number;
  name: string;
  multiple: boolean;
  customized_type: "issue" | "project";
  possible_values?: CustomFieldsListType[];
};

export type ParentType = {
  id: number;
  name: string;
};

export type CustomFieldsListType = {
  label: string;
  value: string;
};

export type TrackerListType = {
  id: number;
  name: string;
};

export type CustomFieldsType = Partial<{
  id: number;
  name: string;
  multiple: boolean;
  value: string[] | string;
}>;

export type IssuesTrackingType = {
  tracker: string;
  open: number;
  resolved: number;
  closed: number;
  total: number;
};

export type ArchiveAction = "archive" | "unarchive";

export type PickedState = PickMainState<
  "adminMenuDivisions" | "buttonLoadCheck" | "errorMessage" | "selectedDivision" | "successMessage"
>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type FilterType = Partial<{
  project: string;
}>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof CARD_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: { project?: string };
  [ACTIONS.CREATE]: { createdData: CreateProjectType };
  [ACTIONS.EDIT]: { projectId: number; editedData: CreateProjectType };
  [ACTIONS.DETAIL]: { projectId: number; limit?: number };
  [ACTIONS.ARCHIVE]: { projectId: number; archiveAction: ArchiveAction };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = any, R = any> = ActionHandler<PickedState & State, Event, Data, P, R>;

/*================================ Constants ==============================*/

export const StateInitial: State = {
  ProjectListSequence: null,
};

export const DataInitial: Data = {};

const Masters = [["division", {}]] as const;

// const LIST_LIMIT = 20;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, CARD_NAME);

export const DEFAULT_LIMIT = 100;

/*================================ Functions ==============================*/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: Masters,
    },
  });

  const [[cfResp, cfErr], [trkResp, trkErr]] = await Promise.all([
    await GetCustomFieldsList(controller, params),
    await GetTrackerList(controller, params),
  ]);

  controller.setState(
    {
      ProjectListSequence: {
        ...state.ProjectListSequence,
        sequenceIndex: "Action",
        customFields: cfResp?.custom_fields ?? [],
        trackerList: trkResp?.trackers ?? [],
      },
    },
    () => Action(controller, { ...params, action: ACTIONS.SEARCH })
  );
};

/*================================ Action ==============================*/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.CREATE]: HandleCreate,
    [ACTIONS.EDIT]: HandleEdit,
    [ACTIONS.DETAIL]: HandleIssuesTracking,
    [ACTIONS.ARCHIVE]: HandleArchive,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/*================================ Handlers ==============================*/

const HandleSearch: Handler<Params<Actions["SEARCH"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING,
    },
  });

  const [projResp, projErr] = await GetProjectList(controller, params);

  if (projErr) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: projErr,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS,
    },
    ProjectListSequence: {
      ...state.ProjectListSequence,
      projectList: projResp.projects || [],
    },
  });
};

const HandleCreate: Handler<Params<Actions["CREATE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CREATE]: BLC_STATUS.LOADING,
    },
  });

  const { createdData } = params;
  const { Division, ...restCreatedData } = createdData as any;
  const customFields = state.ProjectListSequence?.customFields || [];
  const trackerIds = (state.ProjectListSequence?.trackerList || []).map((t) => t.id);

  const defaultCreate: CreateProjectType = {
    is_public: true,
    tracker_ids: trackerIds,
  };

  const projectName = createdData.name;

  let identifier = toKababCase(projectName);

  if (!identifier) {
    identifier = `project-${moment().format("DD-MM-YYYY-HH-mm-ss")}`;
  } else {
    identifier = `${identifier}-${moment().format("DD-MM-YYYY-HH-mm-ss")}`;
  }

  const customFieldNames = ["Division", "Active"];
  const custom_field_values = extractCustomFieldsValues(createdData, customFields, customFieldNames);

  const formatted: CreateProjectPayloadType = {
    project: {
      ...restCreatedData,
      identifier,
      ...defaultCreate,
      custom_field_values: custom_field_values,
      parent_id: CONFIG.REDMINE_MAIN_PROJECT_ID,
      ...(CONFIG.REDMINE_MAIN_PROJECT_ID ? { inherit_members: true } : {}),
    },
  };

  const [resp, err] = await CreateProject(controller, { data: formatted });

  if (err) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.CREATE]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: err,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CREATE]: BLC_STATUS.SUCCESS,
    },
    successMessage: {
      ...state.successMessage,
      [CARD_NAME]: "Project Was Created",
    },
  });

  await HandleSearch(controller, { ...params, action: "SEARCH" });
};

const HandleEdit: Handler<Params<Actions["EDIT"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.EDIT]: BLC_STATUS.LOADING,
    },
  });

  const { projectId, editedData } = params;
  const customFields = state.ProjectListSequence?.customFields || [];
  const customFieldNames = ["Division", "Active"];
  const custom_field_values = extractCustomFieldsValues(editedData, customFields, customFieldNames);

  const formatted: CreateProjectPayloadType = {
    project: {
      name: editedData.name,
      ...(Object.keys(custom_field_values).length > 0 && {
        custom_field_values,
      })
    },
  };

  const [resp, err] = await EditProject(controller, { pk: projectId, data: formatted });

  if (err) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.EDIT]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: err,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.EDIT]: BLC_STATUS.SUCCESS,
    },
    successMessage: {
      ...state.successMessage,
      [CARD_NAME]: "Project Was Edited",
    },
  });

  await HandleSearch(controller, { ...params, action: "SEARCH" });
};

const HandleIssuesTracking: Handler<Params<Actions["DETAIL"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.DETAIL]: BLC_STATUS.LOADING,
    },
  });

  const { projectId, limit } = params;

  const [resp, err] = await GetIssuesTracking(controller, {
    pk: projectId,
    filter: { limit: limit ?? 100 },
  });

  if (err) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.DETAIL]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: err,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.DETAIL]: BLC_STATUS.SUCCESS,
    },
    ProjectListSequence: {
      ...state.ProjectListSequence,
      issuesTracking: resp || [],
    },
  });
};

const HandleArchive: Handler<Params<Actions["ARCHIVE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.ARCHIVE]: BLC_STATUS.LOADING,
    },
  });

  const { projectId, archiveAction } = params;

  const [resp, err] = await ArchiveProject(controller, { pk: projectId, action: archiveAction });

  if (err) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.ARCHIVE]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: err,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.ARCHIVE]: BLC_STATUS.SUCCESS,
    },
    successMessage: {
      ...state.successMessage,
      [CARD_NAME]: archiveAction === "archive" ? "Project Was Archived" : "Project Was Unarchived",
    },
  });

  await HandleSearch(controller, { ...params, action: "SEARCH" });
};

/*================================ APIs ==============================*/

export const GetRedmineToken: Handler = async (controller, params) => {
  const [resp, err] = await GetRedmineTokenApi.get({
    apiToken: controller.apiToken,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetProjectList: Handler = async (controller, params) => {
  const { filter, project } = params;
  const mergedFilter = buildProjectListParams(filter);

  const [resp, err] = project
    ? await getProjectName({ controller, name: `~${project}` })
    : await getProjectList({ controller, params: mergedFilter });

  if (err) return [null, err];

  return [resp, err];
};

const CreateProject: Handler = async (controller, params) => {
  const { data } = params;

  const [resp, err] = await createProject({
    controller,
    data,
  });

  if (err) return [null, err];

  return [resp, err];
};

const EditProject: Handler = async (controller, params) => {
  const { pk, data } = params;

  const [resp, err] = await editProject({
    controller,
    data,
    pk,
  });

  if (err) return [null, err];

  return [resp, null];
};

const ArchiveProject: Handler = async (controller, params) => {
  const { pk, action } = params;

  const [resp, err] = await archiveProject({
    controller,
    pk,
    action,
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetIssuesTracking: Handler = async (controller, params) => {
  const { pk, filter } = params;

  const [resp, err] = await getAllIssuesTracking({
    controller,
    pk,
    ...(filter ? { params: filter } : {}),
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetCustomFieldsList: Handler = async (controller, params) => {
  const [resp, err] = await GetCustomFields.get({
    apiToken: controller.apiToken,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetTrackerList: Handler = async (controller, params) => {
  const [resp, err] = await getTrackerList({
    controller,
    params,
  });

  if (err) return [null, err];

  return [resp, null];
};

/*================================ UTILS ==============================*/

const toKababCase = (str?: string) => {
  if (!str) return "";

  return str
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
};

export const getCustomFieldIdByName = (
  fields: { id: number; name: string; customized_type: string }[],
  name: string,
  type: "project" | "issue" = "project"
) => {
  return fields.find((f) => f.name === name && f.customized_type === type)?.id;
};

const containsSearch = (source: string | undefined, keyword: string) => {
  if (!source) return false;

  return source.toLowerCase().includes(keyword.toLowerCase());
};

const buildProjectListParams = (filter: any = {}) => ({
  include: "trackers",
  limit: DEFAULT_LIMIT,
  ...filter,
});

const extractCustomFieldsValues = (
  source: CreateProjectType,
  customFields: CustomFieldsItemType[],
  fieldNames: string[],
) => {
  const result: Record<number, any> = {};

  fieldNames.forEach(name => {
    const id = getCustomFieldIdByName(customFields, name);
    const value = source.custom_fields?.find(field => field.name === name)?.value;

    if (id && value !== undefined) {
      result[id] = value;
    }
  });

  return result;
}